import i18next from 'i18next';
import { extractBackendMessage } from './extractBackendMessage';
import { emitIndependenceRefusal, independenceRefusalAuditId } from './independenceRefusal';
import { emitToast } from './toastBridge';
import type { ServerStateErrorMeta } from './serverStateMeta';

/**
 * Centralized React Query error → toast + retry policy (Server-state rebuild,
 * docs/DATA_FLOW.md §3, "Error/toast policy"). Wired into the QueryClient's `QueryCache`/`MutationCache`
 * in App.tsx so error UX is consistent without per-hook boilerplate.
 */

/**
 * 401/403 are owned by the axios interceptor (`src/api/API.ts` → forced logout), and the
 * audit route-guard / socket `onAccessDenied` flows also key off them. Surfacing a toast
 * or retrying here would double-handle the same failure, so we always short-circuit.
 */
export const isAuthErrorHandledByInterceptor = (error: unknown): boolean => {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return status === 401 || status === 403;
};

const resolveMessage = (error: unknown, meta?: ServerStateErrorMeta): string =>
  meta?.errorMessage ??
  extractBackendMessage(error) ??
  i18next.t('global.generic_error', {
    ns: 'dashboard',
    defaultValue: 'Something went wrong. Please try again.',
  });

/**
 * Shared policy for queries and mutations. Silent by default — opt in via
 * `meta.showErrorToast` or `meta.errorMessage` — so existing call-site error handling
 * (bespoke toasts, optimistic rollback) is never double-toasted. 401/403 are owned by the
 * axios interceptor. The default flips to mutations-opt-OUT once bespoke toasters migrate.
 *
 * The independence refusal is a question, not a failure (#1215): it is announced to the open
 * dashboard of its audit, which asks it (`useIndependenceGate`), and then not toasted. Off that
 * dashboard nobody asks, and it keeps the policy above.
 */
const handleError = (error: unknown, meta?: ServerStateErrorMeta): void => {
  if (isAuthErrorHandledByInterceptor(error)) return;
  const refusedAuditId = independenceRefusalAuditId(error);
  if (refusedAuditId !== null && emitIndependenceRefusal(refusedAuditId)) return;
  if (meta?.suppressErrorToast) return;
  // A predicate that throws must not take the toast down with it: failing to
  // classify an error is no reason to swallow it.
  try {
    if (meta?.suppressErrorToastFor?.(error)) return;
  } catch {
    /* fall through and toast */
  }
  if (!meta?.showErrorToast && !meta?.errorMessage) return;
  emitToast({ type: meta?.errorToastType ?? 'error', message: resolveMessage(error, meta) });
};

export const handleMutationError = handleError;
export const handleQueryError = handleError;

/**
 * Retry only transient server errors (5xx), capped — never client (4xx) or auth failures,
 * which won't succeed on retry and would race the logout redirect.
 */
export const queryRetryPolicy = (failureCount: number, error: unknown): boolean => {
  if (isAuthErrorHandledByInterceptor(error)) return false;
  const status = (error as { response?: { status?: number } })?.response?.status;
  return typeof status === 'number' && status >= 500 && failureCount < 2;
};
